import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Text,
  Box,
  Tooltip,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useRefreshRoundList, useSelectRound } from "../../QuestionnaireRound/RoundService";
import { useNavigate } from "react-router-dom";
import { Check, EyeClosed, Lock, Plus } from "@phosphor-icons/react";
import { routes } from "../../../../config";
import useAirUserRoles from "../../../../utils/AuthUtils";

/**
 * Round selector button for the sub-header
 */
export const RoundListButton = ({ isOpen, onToggle }) => {
  const roundSelector = useSelector((state) => state.issbRound);

  return (
    <Button
      variant="ghost"
      h="44px"
      fontWeight="500"
      fontSize="sm"
      color="darkTeal"
      borderRadius={0}
      _hover={{ bg: "transparent" }}
      _active={{ bg: "transparent" }}
      rightIcon={isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      onClick={onToggle}
    >
      <Flex align="center" gap={2}>
        {roundSelector?.selectedRound?.name || "Select round"}
        {roundSelector?.selectedRound?.state === "DRAFT" && <EyeClosed size={16} color="var(--chakra-colors-gray-400)" />}
        {roundSelector?.selectedRound?.state === "CLOSED" && <Lock size={16} color="var(--chakra-colors-gray-400)" />}
      </Flex>
    </Button>
  );
};

/**
 * Round list panel content displayed inline below the sub-header
 */
export const RoundPanelContent = ({ onClose }) => {
  const { refreshRoundList } = useRefreshRoundList();
  const navigate = useNavigate();
  const selectRound = useSelectRound();
  const roundSelector = useSelector((state) => state.issbRound);

  const { isOrgAdmin } = useAirUserRoles();

  useEffect(() => {
    refreshRoundList();
  }, [refreshRoundList]);

  const handleSelect = (round) => {
    selectRound(round);
    onClose();
  };

  const handleStartRound = () => {
    onClose();
    navigate(routes.issbQuestionnaireRoundSetup);
  };

  return (
    <Box>
      {isOrgAdmin && (
        <Flex
          as="button"
          align="center"
          gap={3}
          px={4}
          py={3}
          w="100%"
          fontSize="sm"
          fontWeight="500"
          color="darkTeal"
          bg="lightLime"
          cursor="pointer"
          _hover={{ bg: "lightLime" }}
          onClick={handleStartRound}
          position="sticky"
          top={0}
          zIndex={1}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Plus size={16} weight="bold" />
          <Text>Create a new round</Text>
        </Flex>
      )}

      <Box py={1} />
      {roundSelector?.roundList.map((round) => {
        const isSelected = roundSelector?.selectedRound?.id === round.id;
        return (
          <Flex
            key={round.id}
            as="button"
            align="center"
            justifyContent="space-between"
            px={4}
            py={2}
            w="100%"
            fontSize="sm"
            color="gray.700"
            bg={isSelected ? "gray.50" : "transparent"}
            cursor="pointer"
            _hover={{ bg: "gray.50" }}
            onClick={() => handleSelect(round)}
          >
            <Flex align="center" gap={3} flex={1} minW={0} mr="20px">
              <Box w="14px" flexShrink={0}>
                {isSelected && <Check size={14} weight="bold" color="var(--chakra-colors-green-500)" />}
              </Box>
              <Text fontWeight={isSelected ? "600" : "400"} noOfLines={1} textAlign="left">{round.name}</Text>
            </Flex>
            {round.state === "CLOSED" && (
              <Tooltip label="Round Closed" placement="top" openDelay={300}>
                <Box color="gray.400" flexShrink={0}>
                  <Lock size={16} />
                </Box>
              </Tooltip>
            )}
            {round.state === "DRAFT" && (
              <Tooltip label="Round not started" placement="top" openDelay={300}>
                <Box color="gray.400" flexShrink={0}>
                  <EyeClosed size={16} />
                </Box>
              </Tooltip>
            )}
          </Flex>
        );
      })}
    </Box>
  );
};

const RoundList = ({ variant = "outline" }) => {
  // Legacy export kept for the "no round selected" fallback in IssbRequirementsPage
  const { refreshRoundList } = useRefreshRoundList();
  const selectRound = useSelectRound();
  const roundSelector = useSelector((state) => state.issbRound);
  const { isOrgAdmin } = useAirUserRoles();
  const navigate = useNavigate();

  useEffect(() => {
    refreshRoundList();
  }, [refreshRoundList]);

  useEffect(() => {
    if (roundSelector?.roundList.length === 1 && !roundSelector?.selectedRound) {
      selectRound(roundSelector.roundList[0]);
    }
  }, [roundSelector?.roundList, selectRound, roundSelector?.selectedRound]);

  const handleSelect = (round) => {
    selectRound(round);
  };

  const handleStartRound = () => {
    navigate(routes.issbQuestionnaireRoundSetup);
  };

  if (variant === "secondary") {
    return (
      <Flex gap={2} flexWrap="wrap">
        {roundSelector?.roundList.map((round) => (
          <Button
            key={round.id}
            size="sm"
            variant={roundSelector?.selectedRound?.id === round.id ? "solid" : "outline"}
            onClick={() => handleSelect(round)}
          >
            {round.name}
          </Button>
        ))}
      </Flex>
    );
  }

  return null;
};

export default RoundList;
